import { afterNextRender, ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import moment from 'moment/min/moment-with-locales';
import { Moment } from 'moment';
import { DialogActions } from 'src/app/model/dictionary';
import { PACO_DATE_PROVIDERS } from 'src/app/model/paco-date-providers';
import { PacoDialogAction } from 'src/app/components/dialogs/paco-dialog/paco-dialog.component';
import { DataExchangeRequestApiService } from 'src/app/data-transfer/services/data-exchange-request-api-service';

export interface EditDataExchangeRequestDeadlineDialogData {
  requestId: number;
  deadline: string | null;
}

export interface EditDataExchangeRequestDeadlineDialogResult {
  event: DialogActions;
  deadline?: string | null;
}

@Component({
  selector: 'app-edit-data-exchange-request-deadline-dialog',
  templateUrl: './edit-data-exchange-request-deadline-dialog.component.html',
  styleUrls: ['./edit-data-exchange-request-deadline-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: PACO_DATE_PROVIDERS
})
export class EditDataExchangeRequestDeadlineDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<EditDataExchangeRequestDeadlineDialogComponent>);
  private readonly data = inject<EditDataExchangeRequestDeadlineDialogData>(MAT_DIALOG_DATA);
  private readonly dataExchangeRequestApiService = inject(DataExchangeRequestApiService);

  readonly minSelectableDeadline = moment().startOf('day');
  readonly deadlineControl = new FormControl(this.data.deadline ? moment(this.data.deadline) : null);
  readonly isSaving = signal(false);
  readonly errorMessageKey = signal<string | null>(null);
  private readonly isDeadlineValid = signal(this.deadlineControl.valid);

  readonly dialogActions = computed<PacoDialogAction[]>(() => [
    { name: DialogActions.REJECT, labelKey: 'common.buttons.cancel', disabled: this.isSaving() },
    { name: DialogActions.CONFIRM, labelKey: 'common.buttons.ok', color: 'primary', disabled: !this.isDeadlineValid() || this.isSaving() },
  ]);

  constructor() {
    this.deadlineControl.statusChanges.pipe(takeUntilDestroyed()).subscribe(() => this.isDeadlineValid.set(this.deadlineControl.valid));
    afterNextRender(() => this.isDeadlineValid.set(this.deadlineControl.valid));
  }

  onDialogAction(action: DialogActions): void {
    if (action === DialogActions.CONFIRM) {
      this.confirm();
    } else {
      this.cancel();
    }
  }

  clearDeadline(): void {
    this.deadlineControl.setValue(null);
  }

  confirm(): void {
    if (this.deadlineControl.invalid) {
      return;
    }
    const value = this.deadlineControl.value as Moment | null;
    const deadline = value ? value.format('YYYY-MM-DD') : null;
    this.errorMessageKey.set(null);
    this.isSaving.set(true);
    this.dataExchangeRequestApiService.updateDeadline(this.data.requestId, { deadline }, true).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.dialogRef.close({ event: DialogActions.CONFIRM, deadline });
      },
      error: (error: HttpErrorResponse) => {
        this.isSaving.set(false);
        this.errorMessageKey.set(error.status === 409
          ? 'dataManagement.dataExchangeRequests.editDeadlineDialog.closedError'
          : 'dataManagement.dataExchangeRequests.editDeadlineDialog.genericError');
      }
    });
  }

  cancel(): void {
    this.dialogRef.close({ event: DialogActions.REJECT });
  }
}
